import React, { useEffect, useState } from "react";
import axios from "../utils/axiosInstance";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  Breadcrumbs,
  Link,
  IconButton,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Folder,
  InsertDriveFile,
  CloudDownload,
  ArrowUpward,
} from "@mui/icons-material";
import Layout from "./Layout";
import { formatDate } from "../utils/dateUtils";

const Reports = () => {
  const theme = useTheme();
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [filters, setFilters] = useState({ name: "", type: "" });
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [userSorted, setUserSorted] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());

  useEffect(() => {
    const fetchReports = async () => {
      try {
        // Build path starting from reports folder
        const basePath = "reports";
        const fullPath =
          currentPath.length > 0
            ? `${basePath}/${currentPath.join("/")}`
            : basePath;

        const response = await axios.get(`/file/files?path=${fullPath}`);
        setFiles(response.data.contents);
      } catch (error) {
        console.error("Error fetching reports:", error);
      }
    };
    fetchReports();
  }, [currentPath]);

  const handleSort = (key) => {
    setUserSorted(true);
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedFiles = [...files].sort((a, b) => {
    if (!userSorted) {
      // Folders first by default
      if (a.type === "folder" && b.type !== "folder") return -1;
      if (a.type !== "folder" && b.type === "folder") return 1;
    }

    if (a[sortConfig.key] < b[sortConfig.key])
      return sortConfig.direction === "asc" ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key])
      return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const filteredFiles = sortedFiles.filter((file) => {
    return file.name.toLowerCase().includes(filters.name.toLowerCase());
  });

  const handleDownload = async (filePath, fileName) => {
    setDownloadingFiles((prev) => new Set(prev).add(filePath));

    try {
      const response = await axios.get(`/file/download/${filePath}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(filePath);
        return newSet;
      });
    }
  };

  const getFileExtension = (filename) => {
    return filename.split(".").pop().toLowerCase();
  };

  const getFileTypeDisplay = (filename) => {
    const ext = getFileExtension(filename);
    const typeMap = {
      pdf: "PDF Document",
      xlsx: "Excel Spreadsheet",
      csv: "CSV File",
      txt: "Text Document",
      png: "PNG Image",
      jpg: "JPEG Image",
      jpeg: "JPEG Image",
      gif: "GIF Image",
    };
    return typeMap[ext] || ext.toUpperCase();
  };

  return (
    <Layout>
      <Box
        sx={{
          p: 3,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          margin: 0,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ flexShrink: 0 }}>
            <Link
              component="button"
              onClick={() => setCurrentPath([])}
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                "&:hover": { color: theme.palette.primary.main },
              }}
            >
              Reports
            </Link>
            {currentPath.map((segment, index) => (
              <Link
                key={index}
                component="button"
                onClick={() =>
                  setCurrentPath((prev) => prev.slice(0, index + 1))
                }
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": { color: theme.palette.primary.main },
                }}
              >
                {segment}
              </Link>
            ))}
          </Breadcrumbs>

          {/* Search Controls */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexGrow: 1,
              justifyContent: "flex-end",
              maxWidth: 400,
            }}
          >
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search reports..."
              value={filters.name}
              onChange={(e) =>
                setFilters((p) => ({ ...p, name: e.target.value }))
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            />
          </Box>
        </Box>

        {/* Statistics */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {filteredFiles.filter((f) => f.type === "file").length} report
            {filteredFiles.filter((f) => f.type === "file").length !== 1
              ? "s"
              : ""}{" "}
            available
            {filteredFiles.filter((f) => f.type === "folder").length > 0 &&
              ` • ${
                filteredFiles.filter((f) => f.type === "folder").length
              } folder${
                filteredFiles.filter((f) => f.type === "folder").length !== 1
                  ? "s"
                  : ""
              }`}
          </Typography>
        </Box>

        {/* Table Container */}
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
            overflow: "auto",
            maxHeight: "calc(100vh - 240px)",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "50%" }}>
                  <TableSortLabel
                    active={sortConfig.key === "name"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("name")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  <TableSortLabel
                    active={sortConfig.key === "type"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("type")}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  <TableSortLabel
                    active={sortConfig.key === "modified"}
                    direction={sortConfig.direction}
                    onClick={() => handleSort("modified")}
                  >
                    Last Modified
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: "10%" }}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* Parent Folder Navigation */}
              {currentPath.length > 0 && (
                <TableRow
                  hover
                  onClick={() => setCurrentPath((prev) => prev.slice(0, -1))}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ArrowUpward
                        sx={{
                          mr: 1,
                          color: theme.palette.text.secondary,
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Parent Folder
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      Folder
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      -
                    </Typography>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}

              {/* Files and Folders */}
              {filteredFiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                      {filters.name
                        ? "No reports match your search"
                        : "No reports available"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredFiles.map((file, index) => (
                  <TableRow
                    key={index}
                    hover
                    onClick={() =>
                      file.type === "folder" &&
                      setCurrentPath((prev) => [...prev, file.name])
                    }
                    sx={{
                      cursor: file.type === "folder" ? "pointer" : "default",
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {file.type === "folder" ? (
                          <Folder
                            sx={{
                              mr: 1,
                              color: theme.palette.text.secondary,
                            }}
                          />
                        ) : (
                          <InsertDriveFile
                            sx={{
                              mr: 1,
                              color: theme.palette.text.secondary,
                            }}
                          />
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {file.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {file.type === "folder"
                          ? "Folder"
                          : getFileTypeDisplay(file.name)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {formatDate(file.modified)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {file.type !== "folder" && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(file.path, file.name);
                          }}
                          disabled={downloadingFiles.has(file.path)}
                          sx={{
                            color: theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.primary.light + "20",
                            },
                          }}
                          title={`Download ${file.name}`}
                        >
                          {downloadingFiles.has(file.path) ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CloudDownload />
                          )}
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Layout>
  );
};

export default Reports;
